import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";

// ─── City skyline illustration ───────────────────────────────────────────────
function CitySkyline() {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        opacity: 0.11,
        pointerEvents: 'none',
        lineHeight: 0,
      }}
    >
      <svg
        viewBox="0 0 500 220"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', display: 'block' }}
        preserveAspectRatio="xMidYMax meet"
      >
        {/* Back row – shorter buildings */}
        <rect x="0"   y="158" width="28" height="62"  rx="2" fill="white" />
        <rect x="32"  y="132" width="22" height="88"  rx="2" fill="white" />
        <rect x="58"  y="118" width="38" height="102" rx="2" fill="white" />
        <rect x="65"  y="100" width="10" height="120" rx="1" fill="white" />
        <rect x="100" y="148" width="28" height="72"  rx="2" fill="white" />
        <rect x="132" y="88"  width="35" height="132" rx="2" fill="white" />
        <rect x="141" y="66"  width="14" height="154" rx="1" fill="white" />
        <rect x="171" y="122" width="30" height="98"  rx="2" fill="white" />
        <rect x="205" y="60"  width="42" height="160" rx="2" fill="white" />
        <rect x="217" y="40"  width="16" height="180" rx="1" fill="white" />
        <rect x="252" y="108" width="30" height="112" rx="2" fill="white" />
        <rect x="286" y="86"  width="38" height="134" rx="2" fill="white" />
        <rect x="297" y="62"  width="12" height="158" rx="1" fill="white" />
        <rect x="328" y="130" width="28" height="90"  rx="2" fill="white" />
        <rect x="360" y="76"  width="36" height="144" rx="2" fill="white" />
        <rect x="370" y="52"  width="12" height="168" rx="1" fill="white" />
        <rect x="400" y="114" width="32" height="106" rx="2" fill="white" />
        <rect x="436" y="94"  width="30" height="126" rx="2" fill="white" />
        <rect x="470" y="132" width="30" height="88"  rx="2" fill="white" />

        {/* Window grid on key buildings */}
        {[58, 132, 205, 252, 286, 360, 400].map((bx, bi) => (
          [0, 1, 2].map(col =>
            [0, 1, 2, 3].map(row => (
              <rect
                key={`w-${bi}-${col}-${row}`}
                x={bx + 4 + col * 10}
                y={135 + row * 15}
                width={5} height={8}
                rx="1"
                fill="white"
                opacity="0.55"
              />
            ))
          )
        ))}

        {/* Ground line */}
        <rect x="0" y="218" width="500" height="2" fill="white" />
      </svg>
    </Box>
  );
}

// ─── Shared input styling ────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FAFBFC',
    fontSize: '0.9375rem',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#2563EB' },
    '&.Mui-focused fieldset': { borderColor: '#2563EB', borderWidth: '1.5px' },
    transition: 'box-shadow 0.2s ease',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(37,99,235,0.08)' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
};

// ─── Main Component ──────────────────────────────────────────────────────────
function Login() {
  // All state — unchanged
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState("");
  const [roleError, setRoleError]       = useState("");
  const [loading, setLoading]           = useState(false);
  const [loginMethod, setLoginMethod]   = useState("email");
  const [phoneNumber, setPhoneNumber]   = useState("");
  const [otp, setOtp]                   = useState("");
  const [otpSent, setOtpSent]           = useState(false);
  const [timer, setTimer]               = useState(0);

  const { login, googleLogin, sendOtp, verifyOtp } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const isRecruiterLogin = location.pathname === "/recruiter-login";

  // Timer effect — unchanged
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((p) => p - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Submit handler — unchanged
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setRoleError("");
    setLoading(true);

    try {
      const expectedRole = isRecruiterLogin ? "admin" : "candidate";

      if (loginMethod === "email") {
        const result = await login(email, password, expectedRole);

        if (result && result.requiresEmailOtp) {
          sessionStorage.setItem("otp_temp_token",  result.tempToken);
          sessionStorage.setItem("otp_masked_email", result.maskedEmail || "");
          const redirectPath = isRecruiterLogin ? "/admin/dashboard" : "/candidate/dashboard";
          sessionStorage.setItem("otp_return_path", redirectPath);
          navigate("/verify-email-otp");
          return;
        }

        const user = result;
        if (user.role === "super_admin")  navigate("/super-admin/dashboard");
        else if (user.role === "admin")   navigate("/admin/dashboard");
        else                              navigate("/candidate/dashboard");

      } else {
        if (!otpSent) {
          setError("Please request OTP first");
          setLoading(false);
          return;
        }
        const user = await verifyOtp(phoneNumber, isRecruiterLogin ? "recruiter" : "candidate", otp);
        if (user.role === "super_admin")  navigate("/super-admin/dashboard");
        else if (user.role === "admin")   navigate("/admin/dashboard");
        else                              navigate("/candidate/dashboard");
      }
    } catch (err) {
      if (err.response?.data?.code === "ROLE_MISMATCH") {
        setRoleError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || "Failed to login");
      }
    } finally {
      setLoading(false);
    }
  }

  // OTP handler — unchanged
  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await sendOtp(phoneNumber, isRecruiterLogin ? "recruiter" : "candidate");
      setOtpSent(true);
      setTimer(30);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Field change handlers — unchanged
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (roleError) setRoleError("");
    if (error) setError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (roleError) setRoleError("");
    if (error) setError("");
  };

  // Google credential handler — unchanged
  const handleGoogleCredential = async (credential) => {
    setError("");
    setRoleError("");
    setLoading(true);
    try {
      const role = isRecruiterLogin ? "recruiter" : "candidate";
      const result = await googleLogin(credential, role);

      if (result && result.requiresEmailOtp) {
        sessionStorage.setItem("otp_temp_token",   result.tempToken);
        sessionStorage.setItem("otp_masked_email",  result.maskedEmail || "");
        const redirectPath = isRecruiterLogin ? "/admin/dashboard" : "/candidate/dashboard";
        sessionStorage.setItem("otp_return_path", redirectPath);
        navigate("/verify-email-otp");
        return;
      }

      if (result && result.requiresRegistration) {
        sessionStorage.setItem("google_onboarding_data", JSON.stringify(result.googleData));
        navigate("/register?googleOnboarding=1");
        return;
      }

      const user = result;
      if (user.role === "super_admin")   navigate("/super-admin/dashboard");
      else if (user.role === "admin")    navigate("/admin/dashboard");
      else                               navigate("/candidate/dashboard");

    } catch (err) {
      if (err.response?.data?.code === "ROLE_MISMATCH") {
        setRoleError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || "Google Sign-In failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Brand content per login type ─────────────────────────────────────────
  const brand = isRecruiterLogin
    ? {
        badge:          'Recruiter Login',
        line1:          'Hire Smarter',
        highlighted:    'Build Exceptional',
        line2:          'Teams',
        description:    'ExiHire helps recruiters post jobs, conduct AI-powered interviews, evaluate candidates, and build exceptional teams faster.',
      }
    : {
        badge:          'Candidate Login',
        line1:          'Your Next',
        highlighted:    'Opportunity',
        line2:          'Starts Here',
        description:    'ExiHire connects talented candidates with top employers and exciting career opportunities.',
      };

  const pageTitle = isRecruiterLogin ? 'Welcome Back, Recruiter' : 'Welcome Back, Candidate';

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)', mt: 0 }}>

      {/* ════════════════════ LEFT PANEL ════════════════════ */}
      <Box
        sx={{
          width: { md: '38%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          background: 'linear-gradient(160deg, #0B1638 0%, #0D1C40 60%, #0F2050 100%)',
          position: 'relative',
          overflow: 'hidden',
          px: { md: 6, lg: 8 },
          pt: { md: '40px', lg: '48px' },
          pb: 0,
        }}
      >
        {/* Badge — sits directly below the header */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: 'rgba(37,99,235,0.18)',
            border: '1px solid rgba(37,99,235,0.32)',
            borderRadius: '20px',
            px: 2,
            py: 0.625,
            mb: 4,
            width: 'fit-content',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#60A5FA',
              mr: 1,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: '#93C5FD', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.4px' }}
          >
            {brand.badge}
          </Typography>
        </Box>

        {/* Main heading */}
        <Typography
          component="h1"
          sx={{
            color: 'white',
            fontWeight: 800,
            fontSize: { md: '2.5rem', lg: '2.9rem' },
            lineHeight: 1.12,
            mb: 2.5,
            letterSpacing: '-0.5px',
          }}
        >
          {brand.line1}
          <br />
          <Box component="span" sx={{ color: '#2563EB' }}>
            {brand.highlighted}
          </Box>
          <br />
          {brand.line2}
        </Typography>

        {/* Blue accent divider */}
        <Box sx={{ width: 48, height: 4, bgcolor: '#2563EB', borderRadius: 2, mb: 3.5 }} />

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.58)',
            lineHeight: 1.8,
            fontSize: '1rem',
            maxWidth: 340,
          }}
        >
          {brand.description}
        </Typography>

        {/* City skyline illustration */}
        <CitySkyline />
      </Box>

      {/* ════════════════════ RIGHT PANEL ════════════════════ */}
      <Box
        sx={{
          flex: 1,
          bgcolor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 5 },
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 600,   // increased from 480
          }}
        >
          {/* Floating symbol icon — symbol only, no wordmark */}
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(11,22,56,0.13)',
              mb: '-34px',
              zIndex: 1,
              border: '3px solid #FFFFFF',
              outline: '1px solid #E5E7EB',
              flexShrink: 0,
            }}
          >
            <img
              src="/android-chrome-192x192.png"
              alt="ExiHire"
              style={{ height: 40, width: 40, objectFit: 'contain' }}
            />
          </Box>

          {/* ── Login Card ── */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'white',
              borderRadius: '20px',
              pt: '56px',
              pb: '40px',
              px: { xs: '24px', sm: '48px' },
              width: '100%',
              boxShadow: '0 2px 20px rgba(11,22,56,0.08), 0 1px 4px rgba(11,22,56,0.05)',
              border: '1px solid #E8ECF2',
            }}
          >
            {/* Title */}
            <Typography
              variant="h5"
              align="center"
              sx={{
                color: '#0E2A5E',
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                mb: 0.75,
                letterSpacing: '-0.3px',
              }}
            >
              {pageTitle}
            </Typography>

            {/* Subtitle / switch link */}
            <Typography
              variant="body2"
              align="center"
              sx={{ color: '#6B7280', mb: 3, fontSize: '0.875rem' }}
            >
              {!isRecruiterLogin ? (
                <>
                  Are you a Recruiter?{' '}
                  <Link
                    to="/recruiter-login"
                    style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 600 }}
                  >
                    Click here to login
                  </Link>
                </>
              ) : (
                <>
                  Looking for a Job?{' '}
                  <Link
                    to="/login"
                    style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 600 }}
                  >
                    Click here to Candidate Login
                  </Link>
                </>
              )}
            </Typography>

            {/* Error alerts */}
            {error && error !== "Google authentication is not configured." && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.875rem' }}>
                {error}
              </Alert>
            )}
            {roleError && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.875rem' }}>
                {roleError}
              </Alert>
            )}

            <form onSubmit={handleSubmit} noValidate>

              {/* ── Login method toggle ── */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 3,
                  p: 0.75,
                  bgcolor: '#F1F5F9',
                  borderRadius: '14px',
                }}
              >
                {[
                  { method: 'email', label: 'Email', icon: <EmailIcon sx={{ fontSize: 18 }} /> },
                  { method: 'phone', label: 'Phone', icon: <PhoneIphoneIcon sx={{ fontSize: 18 }} /> },
                ].map(({ method, label, icon }) => (
                  <Button
                    key={method}
                    fullWidth
                    onClick={() => { setLoginMethod(method); setError(""); }}
                    startIcon={icon}
                    sx={{
                      borderRadius: '10px',
                      height: 44,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease',
                      ...(loginMethod === method
                        ? {
                            bgcolor: '#0E2A5E',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(14,42,94,0.25)',
                            '&:hover': { bgcolor: '#122F6B' },
                          }
                        : {
                            bgcolor: 'transparent',
                            color: '#6B7280',
                            '&:hover': { bgcolor: 'rgba(14,42,94,0.06)', color: '#0E2A5E' },
                          }),
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>

              {/* ── Email login fields ── */}
              {loginMethod === 'email' ? (
                <>
                  <TextField
                    fullWidth
                    label="Email address"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    margin="normal"
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    margin="normal"
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onMouseDown={() => setShowPassword(true)}
                            onMouseUp={() => setShowPassword(false)}
                            onMouseLeave={() => setShowPassword(false)}
                            sx={{ color: '#9CA3AF', mr: -0.5 }}
                            tabIndex={-1}
                          >
                            {showPassword
                              ? <VisibilityOff sx={{ fontSize: 20 }} />
                              : <Visibility sx={{ fontSize: 20 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              ) : (
                /* ── Phone OTP fields ── */
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      placeholder="10-digit mobile number"
                      value={phoneNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(val);
                        setError("");
                      }}
                      required
                      disabled={otpSent}
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIphoneIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {!otpSent && (
                      <Button
                        variant="contained"
                        onClick={handleSendOtp}
                        disabled={loading || phoneNumber.length !== 10}
                        sx={{
                          height: 56,
                          minWidth: 110,
                          borderRadius: '12px',
                          bgcolor: '#0E2A5E',
                          textTransform: 'none',
                          fontWeight: 600,
                          flexShrink: 0,
                          '&:hover': { bgcolor: '#122F6B' },
                          '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                        }}
                      >
                        Send OTP
                      </Button>
                    )}
                  </Box>

                  {otpSent && (
                    <>
                      <TextField
                        fullWidth
                        label="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(val);
                          setError("");
                        }}
                        margin="normal"
                        required
                        autoFocus
                        sx={inputSx}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 0.75,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                          {timer > 0 ? `Resend OTP in ${timer}s` : ''}
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleSendOtp}
                          disabled={loading || timer > 0}
                          sx={{
                            color: '#2563EB',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            p: '2px 0',
                            minWidth: 'unset',
                          }}
                        >
                          Resend OTP
                        </Button>
                      </Box>
                    </>
                  )}
                </>
              )}

              {/* ── Forgot password ── */}
              {loginMethod === 'email' && (
                <Box sx={{ textAlign: 'right', mt: 0.75, mb: 0.5 }}>
                  <Link
                    to="/forgot-password"
                    style={{ color: '#2563EB', fontSize: '0.8125rem', fontWeight: 500, textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              )}

              {/* ── Login button ── */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                startIcon={!loading ? <LockIcon sx={{ fontSize: 18 }} /> : null}
                disabled={
                  loading ||
                  (loginMethod === 'phone' && (!otpSent || otp.length !== 6))
                }
                sx={{
                  mt: 2.5,
                  height: 52,
                  borderRadius: '12px',
                  bgcolor: '#0E2A5E',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  letterSpacing: '0.2px',
                  boxShadow: '0 4px 14px rgba(14,42,94,0.28)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#122F6B',
                    boxShadow: '0 6px 20px rgba(14,42,94,0.38)',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#CBD5E1',
                    color: '#94A3B8',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                {loading
                  ? 'Please wait…'
                  : loginMethod === 'phone' && otpSent
                    ? 'Verify OTP & Login'
                    : 'Login'}
              </Button>
            </form>

            {/* ── Divider ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3.5, mb: 2.5 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
              <Typography
                variant="caption"
                sx={{ color: '#9CA3AF', fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.8125rem' }}
              >
                or continue with
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
            </Box>

            {/* ── Google Sign-In ── */}
            <GoogleSignInButton
              onCredential={handleGoogleCredential}
              disabled={loading}
              text="continue_with"
            />

            {/* ── Register link ── */}
            <Typography
              variant="body2"
              align="center"
              sx={{ mt: 3.5, color: '#6B7280', fontSize: '0.875rem' }}
            >
              Don't have an account?{' '}
              <Link to="/register" style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 600 }}>
                Register here
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
